import { useEffect, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, PopoverDialog, useForm, usePopoverDialog, DatePickerField, RadioField } from '@frontend/design-system';
import { useAccessibleFaxNumbers } from '../../hooks/use-accessible-fax-numbers';
import { useInboxFiltersShallowStore } from '../../stores/inbox-filters-store';
import { FaxNumbersMenuItem } from './fax-numbers-filter-menu-item';
import { PopoverMenuSection } from './filter-popover-section';
import { TagsFilterMenuItem } from './tags-filter-menu-item';

type FilterPopoverProps = {
  popoverProps: Pick<ReturnType<typeof usePopoverDialog<HTMLButtonElement>>, 'getDialogProps' | 'close'>;
  tags: Tag[];
};

export const FilterPopover = ({ popoverProps, tags }: FilterPopoverProps) => {
  const { t } = useTranslation('fax');
  const { getDialogProps, close } = popoverProps;
  const { selectedLocationIds } = useAppScopeStore();
  const { data: faxNumbers } = useAccessibleFaxNumbers(selectedLocationIds);
  const { inboxFilters, setInboxFilters } = useInboxFiltersShallowStore('inboxFilters', 'setInboxFilters');

  const [faxNumbersField, setFaxNumbersField] = useState<string[]>(inboxFilters.faxNumbers ?? []);
  const [tagsField, setTagsField] = useState<string[]>(inboxFilters.tags ?? []);

  const { getFieldProps, values, seedValues, changedValues, getErrors } = useForm({
    computeChangedValues: true,
    fields: {
      deliveryStatus: {
        type: 'radio',
        value: inboxFilters.isDelivered || '',
      },
      startDate: {
        type: 'datePicker',
        value: inboxFilters.startDate ?? '',
        validator: ({ value }, formValues) => {
          if (!formValues.endDate) return '';
          const startDate = dayjs(value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          return startDate.isAfter(endDate) ? t('Start date must be before the end date') : '';
        },
      },
      endDate: {
        type: 'datePicker',
        value: inboxFilters.endDate ?? '',
        validator: ({ value }, formValues) => {
          if (!formValues.startDate) return '';
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(value, 'MM/DD/YYYY').startOf('day');
          return endDate.isBefore(startDate) ? t('End date must be after the start date') : '';
        },
      },
    },
  });

  useEffect(() => {
    seedValues({
      deliveryStatus: inboxFilters.isDelivered || '',
      startDate: inboxFilters.startDate ?? '',
      endDate: inboxFilters.endDate ?? '',
    });
    setTagsField(inboxFilters.tags ?? []);
    setFaxNumbersField(inboxFilters.faxNumbers ?? []);
  }, [inboxFilters]);

  const handleSubmit = () => {
    setInboxFilters({
      isDelivered: values.deliveryStatus || '',
      tags: tagsField ?? [],
      faxNumbers: faxNumbersField ?? [],
      startDate: values.startDate ?? '',
      endDate: values.endDate ?? '',
    });
    close();
  };

  const errors = getErrors();
  const isError = !!errors.startDate || !!errors.endDate;

  const faxNumbersFieldChanged = !isEqual(faxNumbersField, inboxFilters.faxNumbers ?? []);
  const tagsFieldChanged = !isEqual(tagsField, inboxFilters.tags ?? []);

  const disableApply =
    isError ||
    ((!changedValues || Object.keys(changedValues).every((key) => key === 'undefined')) &&
      !faxNumbersFieldChanged &&
      !tagsFieldChanged);

  const handleSelectTags = (selectedTags: string[] = []) => {
    const availableTagsCount = tags.length;
    const updatedTags =
      availableTagsCount === tagsField.length && selectedTags.length === availableTagsCount
        ? []
        : [0, availableTagsCount].includes(selectedTags.length)
        ? selectedTags
        : tagsField.includes(selectedTags[0])
        ? tagsField.filter((n) => n !== selectedTags[0])
        : [...tagsField, selectedTags[0]];
    setTagsField(updatedTags);
  };

  const handleSelectFaxNumbers = (selectedNumbers: string[]) => {
    const availableFaxNumbersCount = faxNumbers.length;
    const updatedNumbers =
      availableFaxNumbersCount === faxNumbersField.length && selectedNumbers.length === availableFaxNumbersCount
        ? []
        : [0, availableFaxNumbersCount].includes(selectedNumbers.length)
        ? selectedNumbers
        : faxNumbersField.includes(selectedNumbers[0])
        ? faxNumbersField.filter((n) => n !== selectedNumbers[0])
        : [...faxNumbersField, selectedNumbers[0]];
    setFaxNumbersField(updatedNumbers);
  };

  return (
    <PopoverDialog
      {...getDialogProps()}
      css={{
        padding: theme.spacing(1, 2),
        borderRadius: theme.borderRadius.medium,
      }}
      returnFocus={false}
    >
      <>
        <PopoverMenuSection
          title={t('Delivery Status')}
          onClear={() => {
            getFieldProps('deliveryStatus').onChange({ name: 'deliveryStatus', value: '' });
          }}
          clearDisabled={!values.deliveryStatus}
        >
          <RadioField
            {...getFieldProps('deliveryStatus')}
            css={{ width: '100%', cursor: 'pointer', label: { cursor: 'pointer' }, input: { cursor: 'pointer' } }}
          >
            <RadioField.Radio value='delivered'>{t('Delivered')}</RadioField.Radio>
            <RadioField.Radio value='undelivered'>{t('Undelivered')}</RadioField.Radio>
          </RadioField>
        </PopoverMenuSection>
        <PopoverMenuSection
          title={t('Send Date')}
          onClear={() => {
            getFieldProps('startDate').onChange({ name: 'startDate', value: '' });
            getFieldProps('endDate').onChange({ name: 'endDate', value: '' });
          }}
          clearDisabled={!values.startDate && !values.endDate}
        >
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <DatePickerField {...getFieldProps('startDate')} label={t('Start Date')} />
            <DatePickerField {...getFieldProps('endDate')} label={t('End Date')} />
          </div>
        </PopoverMenuSection>
        <PopoverMenuSection
          title={t('Other Filters')}
          onClear={() => {
            setFaxNumbersField([]);
            setTagsField([]);
          }}
          clearDisabled={!tagsField?.length && !faxNumbersField?.length}
        >
          {faxNumbers?.length > 1 && (
            <FaxNumbersMenuItem
              selectedFaxNumbers={faxNumbersField}
              selectFaxNumbers={handleSelectFaxNumbers}
              faxNumbers={faxNumbers}
            />
          )}
          <TagsFilterMenuItem
            tags={tags}
            selectedTags={tagsField}
            selectTags={handleSelectTags}
            closeParentMenu={close}
          />
        </PopoverMenuSection>
      </>
      <div css={{ padding: theme.spacing(1, 0) }}>
        <Button css={{ width: '100%' }} disabled={disableApply} onClick={handleSubmit}>
          {t('Apply')}
        </Button>
      </div>
    </PopoverDialog>
  );
};
